import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Clapperboard, ChevronRight, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import AuditionFormDialog from "@/components/crm/AuditionFormDialog";
import BriefingDialog from "@/components/crm/BriefingDialog";
import { formatDate } from "@/lib/crm";

function StatusBadge({ status, t }) {
  const map = {
    new: "bg-stone-800 text-stone-300",
    in_preparation: "bg-amber-500/20 text-amber-300",
    sent: "bg-blue-500/20 text-blue-300",
    callback: "bg-fuchsia-500/20 text-fuchsia-300",
    booked: "bg-emerald-500/20 text-emerald-300",
    not_booked: "bg-rose-500/20 text-rose-300",
  };
  return (
    <span className={`mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${map[status] || map.new}`}>
      {t(`aud.status.${status}`)}
    </span>
  );
}

export default function AuditionsView({ t, lang }) {
  const nav = useNavigate();
  const [auditions, setAuditions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [briefingAudition, setBriefingAudition] = useState(null);
  const [prevAuditions, setPrevAuditions] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get("/crm/auditions"),
        api.get("/crm/contacts"),
      ]);
      setAuditions(a.data || []);
      setContacts(c.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const castingDirectors = useMemo(
    () => contacts.filter((c) => c.type === "casting_director"),
    [contacts],
  );

  const openNew = () => {
    if (castingDirectors.length === 0) {
      toast.error(t("aud.form.no_cd_msg"));
      return;
    }
    setFormOpen(true);
  };

  const onCreated = async (audition) => {
    // Fetch prior auditions with the same casting director (excluding the one just created)
    try {
      const r = await api.get(`/crm/contacts/${audition.casting_director_id}/auditions`);
      const prev = (r.data || []).filter((a) => a.audition_id !== audition.audition_id);
      setPrevAuditions(prev);
    } catch { setPrevAuditions([]); }
    setBriefingAudition(audition);
    await load();
  };

  const del = async (a) => {
    if (!window.confirm(t("aud.confirm_delete"))) return;
    try {
      await api.delete(`/crm/auditions/${a.audition_id}`);
      setAuditions((prev) => prev.filter((x) => x.audition_id !== a.audition_id));
      toast.success(t("aud.deleted"));
    } catch { toast.error(t("aud.delete_fail")); }
  };

  return (
    <>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="mono text-[11px] tracking-[0.3em] text-stone-500 uppercase">
          {t("aud.list_title")}
        </div>
        <button data-testid="aud-new-btn" onClick={openNew} className="pill-btn">
          <Plus size={16} /> {t("aud.new")}
        </button>
      </div>

      {castingDirectors.length === 0 && (
        <div className="paper p-5 border-amber-500/30 text-sm text-amber-300 bg-amber-500/5">
          {t("aud.form.no_cd_msg")}
        </div>
      )}

      {loading ? (
        <div className="mono text-stone-500 text-sm">{t("dash.loading")}</div>
      ) : auditions.length === 0 ? (
        <div className="paper p-10 text-center">
          <Clapperboard className="mx-auto text-stone-500" />
          <div className="serif text-2xl text-stone-200 mt-3">{t("aud.empty")}</div>
          {castingDirectors.length > 0 && (
            <button onClick={openNew} className="pill-btn mt-6" data-testid="aud-empty-new-btn">
              <Plus size={14} /> {t("aud.new")}
            </button>
          )}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="paper !p-0"
          data-testid="aud-table"
        >
          <div className="hidden lg:grid grid-cols-12 gap-3 px-4 py-3 mono text-[10px] uppercase tracking-widest text-stone-500 border-b border-stone-900">
            <div className="col-span-3">{t("aud.col.project")}</div>
            <div className="col-span-2">{t("aud.col.role")}</div>
            <div className="col-span-2">{t("aud.col.type")}</div>
            <div className="col-span-2">{t("aud.col.cd")}</div>
            <div className="col-span-2">{t("aud.col.next_date")}</div>
            <div className="col-span-1">{t("aud.col.status")}</div>
          </div>
          <ul>
            {auditions.map((a) => (
              <li
                key={a.audition_id}
                data-testid={`aud-row-${a.audition_id}`}
                onClick={() => nav(`/crm/auditions/${a.audition_id}`)}
                className="grid grid-cols-12 gap-3 items-center px-4 py-4 border-t border-stone-900 hover:bg-stone-900/40 transition-colors cursor-pointer"
              >
                <div className="col-span-12 lg:col-span-3">
                  <div className="serif text-lg text-stone-100 hover:text-amber-400">{a.project}</div>
                </div>
                <div className="col-span-6 lg:col-span-2 text-sm text-stone-300 truncate">{a.role || "—"}</div>
                <div className="col-span-6 lg:col-span-2 mono text-[11px] uppercase tracking-widest text-stone-400 truncate">
                  {t(`aud.type.${a.audition_type}`)}
                </div>
                <div className="col-span-6 lg:col-span-2 text-sm text-stone-300 truncate">
                  {a.casting_director_name || "—"}
                </div>
                <div className="col-span-4 lg:col-span-2 text-xs text-stone-400">
                  {a.next_date ? formatDate(a.next_date, lang) : t("aud.no_next_date")}
                </div>
                <div className="col-span-2 lg:col-span-1 flex items-center gap-2">
                  <StatusBadge status={a.status} t={t} />
                </div>
                <div className="col-span-12 flex items-center justify-end gap-1 mt-1 lg:mt-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); del(a); }}
                    data-testid={`aud-delete-${a.audition_id}`}
                    className="ghost-btn !py-1.5 !px-2 text-xs hover:!text-rose-400 hover:!border-rose-500/50"
                  >
                    <Trash2 size={12} />
                  </button>
                  <ChevronRight size={14} className="text-stone-500" />
                </div>
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      <AuditionFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        castingDirectors={castingDirectors}
        onCreated={onCreated}
      />

      <BriefingDialog
        open={!!briefingAudition}
        onOpenChange={(v) => !v && setBriefingAudition(null)}
        audition={briefingAudition}
        prevAuditions={prevAuditions}
      />
    </>
  );
}
